package org.multiplatform.kotfolio.pages.kotlin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/kotlin/higher_order_functions")
@Composable
fun HigherOrderFunctionsKotlinPage() {
    BlogLayout(
        PageProperties(
            "Higher Order Functions",
            "Higher-Order Function is any function that either accepts another function body as a parameter or returns a function body. If it does at least one of these (or both), it earns the title of Higher-Order."
        ),
        BlogProperties("kotlin", "Higher Order Functions")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            //A function that accepts another function as an argument or returns one.

            SpanText(
                text = "Higher-Order Function is any function that either accepts another function body as a parameter or returns a function body. If it does at least one of these (or both), it earns the title of “Higher-Order.”",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "A high order function (Higher level function) is a function that accepts a function as a parameter returns a function or can do both. (A function that accepts another function as an argument or returns one)",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Meaning, instead of passing Int, string, or other types as a parameter in a function we can pass a function as a parameter in another function.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            KotlinCode(
                code = """
                    fun addition (a: Int, b: Int, c: (Int, Int) -> Int): Int {
                        return c.invoke(a, b)
                    }
                    
                    fun main() {
                        val result = addition(a = 2, b = 5) { c, d ->
                            c.plus(d)
                        }
                        
                        println(result)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Function body return 
                    
                    //1
                    fun add3(a: Int, b: Int): (Int, Int) -> Int {
                        return { c, d ->
                            c.plus(d)
                        }
                    }
                    
                    fun main() {
                        val result = add3(2, 4)
                        print(result.invoke(5, 7))
                    }
                    
                    //2
                    fun add3(a: Int, b: Int): (Int, Int) -> Int {
                        return { c, d ->
                            a.plus(b)
                        }
                    }
                    
                    fun main() {
                        val result = add3(2, 4)
                        print(result.invoke(5, 7))
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val numbers = listOf(1, 2, 3, 4, 5)
                    val evenNumbers = numbers.filter { it % 2 == 0 }
                    println(evenNumbers) // Output: [2, 4]
                    """.trimIndent()
            )

            KotlinCode(
                code = $$$"""
                    /Define the Higher-Order Function
                    fun calculateTotal(
                        prices: List<Double>, 
                        calculator: (List<Double>) -> Double
                    ): Double {
                        return calculator(prices)
                    }
                    
                    //Create a Lambda Expression for Calculation
                    val sumCalculator: (List<Double>) -> Double = { prices ->
                        prices.sum()
                    }
                    
                    //Call the Higher-Order Function
                    val prices = listOf(12.99, 8.75, 24.50, 10.0)
                    val totalAmount = calculateTotal(prices, sumCalculator)
                    println("Total amount: \$${totalAmount}")
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Dynamic Dispatch: Command Pattern — High-order functions facilitate dynamic dispatch, allowing the creation of flexible command-based systems.
                    
                    interface Command {
                        fun execute()
                    }

                    class RoyalCommand(private val action: () -> Unit) : Command {
                        override fun execute() {
                            println("Royal Commander: Execute command!")
                            action()
                        }
                    }

                    // Example Usage
                    val royalOrders = listOf(
                        RoyalCommand { println("Knight: Charge!") },
                        RoyalCommand { println("Mage: Cast Spell!") },
                        RoyalCommand { println("Archer: Ready Aim Fire!") }
                    )

                    royalOrders.forEach { it.execute() }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Data Transformation: Custom Mapping Logic — High-order functions simplify data transformation, abstracting mapping details and improving code clarity.
                    
                    data class Product(val name: String, val price: Double)

                    val products = listOf(
                        Product("Sword", 150.0), 
                        Product("Shield", 80.0), 
                        Product("Potion", 20.0)
                    )

                    // Example Usage
                    val discountedProducts = products.map { 
                        it.copy(price = it.price * 0.8) 
                    }
                    """.trimIndent()
            )

            //https://medium.com/dev-genius/kotlin-lambda-with-receiver-c8de99364201
            KotlinCode(
                code = $$"""
                    fun List<Int>.avg(): Double = if (isEmpty()) 0.0 else sum().toDouble()/size
                    
                    fun List<Int>.mode(): Int? {
                        if (isEmpty()) return null    
                        return groupingBy { it }.eachCount().maxByOrNull { it.value }
                            ?.let { (value, _) -> value }
                    }
                    
                    fun List<Int>.median(): Double? {
                        if (isEmpty()) return null
                        val sorted = sorted()
                        val mid = sorted.size / 2
                        return if (size % 2 == 1) sorted[mid].toDouble()
                        else (sorted[mid - 1] + sorted[mid]) / 2.0 //if even count of list size
                    }
                    
                    inline fun <T> T.doWith(block: T.() -> Unit) {
                        block(this)
                    }
                    
                    fun main() {
                        val numbers = listOf(1, 3, 3, 4, 5, 5, 5, 7, 8)
                        numbers.doWith {
                            println("Count  : $size")
                            println("Sum    : ${sum()}")
                            println("Average: ${avg()}")
                            println("Mean   : ${median()}")
                            println("Mode   : ${mode() ?: "No mode (all values unique)"}")
                        }
                    }
                    """.trimIndent()
            )

            //https://medium.com/dev-genius/kotlin-lambda-with-receiver-c8de99364201
            KotlinCode(
                code = """
                    class Validator {
                        private val errors = mutableListOf<String>()
                        fun add(condition: Boolean, message: String) {
                            if (!condition) errors.add(message)
                        }
                        fun validate(): List<String> = errors
                    }
                    
                    fun validate(block: Validator.() -> Unit): List<String> {
                        val validator = Validator()
                        validator.block()
                        return validator.validate()
                    }
                    
                    fun main() {
                        val errors = validate {
                            add(1 > 2, "Invalid comparison")
                            add("".isNotEmpty(), "Empty"); add("".isNotBlank(), "Blank absent")
                        }
                        println(errors)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Two important points",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul(Modifier.margin(topBottom = 0.8.em).toAttrs()) {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When we pass input as a function follow this syntax fn: -> fn is just a naming convention you can add any name",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = ":: when we pass a function in the main class",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                                    fun main(){
                                        higherOrderFunction(10, 34, ::addTwoNumber)
                                    }

                                    fun higherOrderFunction(a: Int, b: Int, fn: (Int, Int) -> Int){
                                        println(fn(a, b))
                                    }

                                    fun addTwoNumber(a: Int, b:Int): Int {
                                        return  a+b
                                    }
                                """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Map is a higher-order function that transforms a data structure. It does not change the existing data structure but returns a new one, hence, it can be applied to an immutable data structure.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        fun squareADigit(number: Int): Int {
                            return number * number
                        }
                                     
                        fun main() {
                            val list = listOf<Int>(1, 2, 3, 4, 5, 6, 7)
                            //an alternate approach since "map" is a higher order function
                            val squaredList = list.map(::squareADigit)
                            println(squaredList)
                        }
                                """.trimIndent()
                )

                KotlinCode(
                    code = """
                        // Higher-order function
                        fun operateOnNumbers(a: Int, b: Int, operation: (Int, Int) -> Int): Int {
                            return operation(a, b)
                        }

                        // Using the higher-order function
                        fun main() {
                            val sum = operateOnNumbers(5, 3) { x, y -> x + y }
                            println(sum) // Output: 8
                        }
                        // Here, we are passing a lambda expression as the function body to the higher-order function.
                        // The lambda { x, y -> x + y } defines the behavior of the "operation" parameter.
                        // Instead of defining a separate function, we directly provide the function body.
                        // This means operateOnNumbers will execute this lambda using 5 and 3 as arguments.
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        // Original List
                        val numbers = listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10)

                        // High-order function with lambda to filter even numbers
                        val evenNumbers = numbers.filter { it % 2 == 0 }

                        // Result
                        println(evenNumbers) // Output: [2, 4, 6, 8, 10]
                                """.trimIndent()
                )

                KotlinCode(
                    code = """
                        // Original List
                        val names = listOf("Alice", "Bob", "Charlie", "David")

                        // High-order function with lambda to transform names to uppercase
                        val uppercaseNames = names.map { it.toUpperCase() }

                        // Result
                        println(uppercaseNames) // Output: [ALICE, BOB, CHARLIE, DAVID]
                                """.trimIndent()
                )
            }

            SpanText(
                text = "Assigning Functions to Variables (Lambda Expressions)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul(Modifier.margin(topBottom = 0.8.em).toAttrs()) {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You can define logic within curly braces and assign it to a variable. If the function takes only one parameter, Kotlin provides the it keyword to simplify the syntax.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    // A variable holding a function that takes a String and returns a formatted String
                    val formatUsername: (String) -> String = { name ->
                        "User: $name" // The last line is the return value
                    }

                    // Using the 'it' keyword for a single parameter to make it cleaner
                    val greetUser: (String) -> Unit = {
                        println("Hello, $it!") // 'it' refers to the single String parameter
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Anonymous Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul(Modifier.margin(topBottom = 0.8.em).toAttrs()) {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Anonymous functions and lambda expressions are both unnamed functions that can be passed as values. However, anonymous functions use the ‘fun’ keyword, while lambda expressions are defined with curly braces {}.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝘞𝘦 𝘤𝘢𝘯 𝘤𝘳𝘦𝘢𝘵𝘦 𝘪𝘯 𝘵𝘸𝘰 𝘸𝘢𝘺𝘴: 1. 𝘞𝘪𝘵𝘩𝘰𝘶𝘵 𝘳𝘦𝘤𝘦𝘪𝘷𝘦𝘳 2. 𝘞𝘪𝘵𝘩 𝘳𝘦𝘤𝘦𝘪𝘷𝘦𝘳 𝘭𝘪𝘬𝘦 𝘦𝘹𝘵𝘦𝘯𝘴𝘪𝘰𝘯 𝘧𝘶𝘯𝘤𝘵𝘪𝘰𝘯",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sometimes you want a more traditional function look without giving it a formal name. This is useful when you need to explicitly define return types.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }


            KotlinCode(
                code = """
                 // An anonymous function assigned to a variable
                 val multiplyByTwo = fun(number: Int): Int {
                     return number * 2 // Explicit return statement used here
                 }
            """.trimIndent()
            )

            KotlinCode(
                code = """
                val strengthWithoutReceiver = fun(input: String) : Int {
                    return input.length
                }

                val strengthWithReceiver = fun String.() : Int {
                    return this.length
                }

                fun main() {
                    println(strengthWithoutReceiver("Hello"))
                    println("Hello".strengthWithReceiver())
                }
                        """.trimIndent()
            )

            KotlinCode(
                code = """
                fun main() {
                    println(joinString("Jetpack", "Compose"))
                }
                val joinString = fun(a:String, b: String): String { return a + b }
                        """.trimIndent()
            )

            SpanText(
                text = "Object Expressions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The object keyword can also be used to create objects of an anonymous class known as anonymous objects. They are used if you need to create an object of a slight modification of some class or interface without declaring a subclass for it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     open class Person() {
                        fun eat() = println("Eating food.")

                        fun talk() = println("Talking with people.")

                        open fun pray() = println("Praying god.")
                     }

                     fun main(args: Array<String>) {
                        val atheist = object : Person() {
                            override fun pray() = println("I don't pray. I am an atheist.")
                        }

                        atheist.eat()
                        atheist.talk()
                        atheist.pray()
                     }
                """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you are implementing a class that has a constructor to declare an anonymous object, you need to pass appropriate constructor parameters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    open class Person(name: String, age: Int) {

                    init {
                        println("name: $name, age: $age")
                    }

                        fun eat() = println("Eating food.")
                        fun talk() = println("Talking with people.")
                        open fun pray() = println("Praying god.")
                    }

                    fun main(args: Array<String>) {
                        val atheist = object : Person("Jack", 29) {
                            override fun pray() = println("I don't pray. I am an atheist.")
                        }

                        atheist.eat()
                        atheist.talk()
                        atheist.pray()
                    }
                """.trimIndent()
            )

            SpanText(
                text = "Callable References / Function References and Execution",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When calling a function that requires a functional parameter, you can pass a lambda directly or use a Function Reference (::) if the function already exists elsewhere.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "References to functions, properties, and constructors can also be called or used as instances of function types.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Function references in Kotlin enable referencing a function by its name without executing it. They offer a means to pass functions as arguments or store them in variables. Function references prove valuable when treating functions as first-class citizens, allowing them to be passed around as data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    fun printLog(message: String) {
                        println("Log: $message")
                    }

                    fun processData(data: String, action: (String) -> Unit) {
                        action(data) // Executing the function passed as a parameter
                    }

                    fun main() {
                        // 1. Passing a lambda directly
                        processData("Data point A") { print(it) }

                        // 2. Using a function reference (::) to point to printLog
                        processData("Data point B", ::printLog) 
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun sweet() {
                        println("Hello, World!")
                    }

                    val functionReference = ::sweet

                    fun main() {
                        functionReference() // Output: Hello, World!
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun isOdd(x: Int) = x % 2 != 0

                    val numbers = listOf(1, 2, 3)
                    println(numbers.filter(::isOdd))
                """.trimIndent()
            )


            KotlinCode(
                code = """ 
                    val words = listOf( "Kotlin", "is", "fun")
                    val lengths = words.map(String:: length)
                    println(lengths)
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Property References

                    data class Person(val name: String, val age: Int)
                    val people = listOf (Person("Alice", 29), Person( "Bob", 31))
                    val names = people.map (Person:: name )
                    println (names) // Output: [Alice, Bob]
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Constructor References

                    data class Person(val name: String, val age: Int)
                
                    val createPerson = ::Person
                    val person = createPerson ("Charlie", 30)
                    println(person) // Output: Person (name=Charlie, age=30)
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Constructor references can be used in factory patterns or when creating instances dynamically

                    val personFactory: (String, Int) -> Person = :: Person
                    val newPerson = personFactory( "Dana", 25)
                    println(newPerson) // Output: Person (name-Dana, age=25
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Bound and Unbound  References

                    val alice = Person("Alice", 29)
                    val getName = alice: name // Bound reference
                    println (getName()) // Output: Alice

                    val getAge = Person::age // Unbound reference
                    println (getAge(alice)) // Output: 29
                """.trimIndent()
            )

            SpanText(
                text = "Receivers and Infix Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin allows us to define functional parameters that belong to a specific class context, making the code read almost like a natural language.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Functional Parameters with Receivers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By using the ClassName.() -> Unit syntax, we can treat the function parameter as a member of that class inside the HOF body.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class NetworkManager {
                        fun connect() = println("Connected to server.")
                    }

                    // The 'setup' parameter is a function that belongs to NetworkManager
                    fun performNetworkOperation(manager: NetworkManager, setup: NetworkManager.() -> Unit) {
                        manager.setup() // We can call 'setup' as if it were a member of manager
                    }

                    fun main() {
                        val myManager = NetworkManager()
                        performNetworkOperation(myManager) {
                            connect() // We have direct access to NetworkManager's methods here
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Higher Order Functions inline function Vs Inline Function",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "higher-order functions or lambda expressions are all stored as an object so memory allocation for both function objects and classes, and virtual calls might introduce runtime overhead.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "inline function is a feature that can be used to reduce the overhead of function calls, especially in scenarios where higher-order functions are frequently used.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Infix Higher - Order Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An infix function in Kotlin is a function that is defined with the infix keyword and takes one parameter.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The function is either defined on a class/member function or is an extension method for a class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "infix function is a function that allows you to call it using a special syntax without using dot and parentheses.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The parameter cannot be a variable argument",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The parameter cannot have a default value",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead, you place the function name between the object and its argument, enhancing code readability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It allows you to call the function using a special infix notation, making your code more readable and natural.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When to Use Infix Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creating domain specific languages",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expressing logical relationships clearly",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "infix functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Infix extensions are a special type of function in Kotlin that let you call them without the usual dots and parentheses, making your code look like a sentence. Infix functions are used to make code more readable and expressive by allowing you to call functions using a special infix notation. They are often used to create more natural and human-readable code, especially for operations that are commonly expressed as binary operations (e.g., mathematical operations, combining words).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An infix function is a member function or extension function that has a single parameter when called using infix notation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    //Infix Function
                    infix fun Int.add(x: Int): Int {
                        return this + x
                    }

                    fun main() {
                        val result = 2 add 3 // Using infix function
                        println("Result: $result") // Output: Result: 5
                    }

                    //Infix Functions with Custom Classes
                    class Point(val x: Int, val y: Int) {
                        infix fun at(other: Point): Point {
                            return Point(x + other.x, y + other.y)
                        }
                    }

                    val point1 = Point(1, 2)
                    val point2 = Point(3, 4)
                    val combinedPoint = point1 at point2 // Equivalent to: point1.at(point2)

                """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                     data class Medicine(val name: String, val interactions: List<String>)

                     infix fun Medicine.interactsWith(other: Medicine): Boolean {
                         return this.interactions.contains(other.name) || other.interactions.contains(this.name)
                     }

                     fun main() {
                         val ibuprofen = Medicine("Ibuprofen", listOf("Aspirin"))
                         val aspirin = Medicine("Aspirin", listOf("Ibuprofen"))

                         val risky = ibuprofen interactsWith aspirin
                         println("Risky combination? $risky")
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                     fun main() {
                         val math = Math()

                         val result = math square 3
                         println(result)

                         val chk = Check()
                         println(chk dataType 3.3)
                     }

                     private class Math {

                         infix fun square(n: Int): Int {
                             val num = n * n
                             return num
                         }
                     }

                     private class Check {

                         infix fun dataType(x: Any): Any {
                             return when (x) {
                                 is String -> "String"
                                 is Int -> "Integer"
                                 is Double -> "Double"
                                 else -> "invalid"
                             }
                         }
                     }
                """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can combine infix notation with HOFs to create a Domain Specific Language (DSL) feel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    // An infix function that takes another function as a parameter
                    infix fun String.modifyWith(transform: (String) -> String) {
                        val result = transform(this)
                        println("Transformed result: $result")
                    }

                    fun main() {
                        // Calling the function using infix notation (no dots or parentheses)
                        "Kotlin" modifyWith { it.uppercase() }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "inner class",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A class declared inside another using the inner keyword that can access the fields of the outer class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An inner class is also a nested class because it is embedded inside another instead of being outside by its own, the only difference is that by having the inner keyword, we are able to access the outer class properties.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To call the method in the inner class you have to create a Person( ) instance because the inner class is linked to a specific instance of the outer class, that is why it can access its methods and fields.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     class Person {
                         private var glucoseLevel: Int = 100

                         inner class Diet {
                             fun recommendDiet() = print("Recommend a diet based on ${'$'}{'$'}glucoseLevel")
                         }
                     }

                     // As inner class
                     var personDiet = Person().Diet().recommendDiet()
                """.trimIndent()
            )

            SpanText(
                text = "Inline Keyword",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you use the inline keyword on a function, you are telling the compiler to copy the code of that function directly into the place where it is called and to not allocate memory for it, therefore saving resources. inline keyword is to instruct the compiler to copy-paste the function's bytecode directly into the call site, rather than creating a separate function call. This can improve performance by eliminating function call overhead and enabling other optimizations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Useful to improve performance by avoiding the overhead of creating objects for lambdas or function objects. But your app might be a bit bigger if you use this function a lot.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    inline fun <T> List<T>.customFilter(predicate: (T) -> Boolean): List<T> {
                        val result = mutableListOf<T>()
                        for (item in this) {
                            if (predicate(item)) result.add(item)
                        }
                        return result
                    }

                    fun main() {
                        val numbers = listOf(1, 2, 3, 4, 5)
                        val evenNumbers = numbers.customFilter { it % 2 == 0 }
                        println(evenNumbers) // [2, 4]
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                     inline fun repeatAction(times: Int, action: () -> Unit) {
                         for (i in 1..times) {
                             action()
                         }
                     }

                     fun main() {
                         repeatAction(3) {
                             println("Hello, Kotlin!")
                         }
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    inline fun logExecution(tag: String, action: () -> Unit) {
                        println("Execution started")
                        action()
                        println("Execution ended")
                    }

                    fun main() {
                        logExecution("TEST") {
                            println("Performing some task")
                        }
                    }
                """.trimIndent()
            )

            SpanText(
                text = "Normal function Vs Inline Function",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Write a normal function without using the inline keyword.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class InlineFunctionDemo {

                        fun firstInlineFunction() {
                            println("This is first Inline function")
                        }

                        fun secondInlineFunction() {
                            firstInlineFunction()
                            println("This is second Inline function")
                        }
                    }

                    fun main() {
                        val inlineFunctionDemo = InlineFunctionDemo()
                        inlineFunctionDemo.secondInlineFunction()
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "As we can see in the Kotlin bytecode, inside the secondInlineFunction() called firstInlineFunction(), we will now add an inline keyword for the firstInlineFunction() and see the behavior.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class InlineFunctionDemo {

                        inline fun firstInlineFunction() {
                            println("This is first Inline function")
                        }

                        inline fun secondInlineFunction() {
                            firstInlineFunction()
                            println("This is second Inline function")
                        }
                    }

                    fun main() {
                        val inlineFunctionDemo = InlineFunctionDemo()
                        inlineFunctionDemo.secondInlineFunction()
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "As you can see the body of firstInlineFunction() is copied to where it was called. as discussed earlier the function object is not created because the body of the inline function is copied directly to the place where it is called.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "The Performance Problem",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In standard Kotlin, every time you pass a lambda { } into a function, the compiler secretly creates an anonymous Function class object in memory behind the scenes to represent that lambda. If you call this function inside a loop 10,000 times, you instantly create 10,000 useless objects in RAM, triggering heavy Garbage Collection (Jank).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Inline Function - (.map{}, .filter{}, .forEach{})",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The compiler can insert the function body at the call site, reducing some lambda/object allocation overhead.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you mark a function as inline, you command the Kotlin compiler to stop creating objects in memory. Instead, during compilation, the compiler physically takes the actual lines of code inside the call site.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine you’re a chef working in a restaurant kitchen. You have a recipe that includes a special cooking technique, like a particular way to season a dish. Instead of writing down the entire seasoning process every time you cook, you create a “seasoning guide” that you can quickly refer to and use directly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Inline function is a kind of function when used, the compiler does not allocate a memory for the function. Instead, the compiler copies the piece of code and execute at runtime.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The inline keyword tells the compiler to copy the code of the HOF and the lambda directly into the call site, eliminating the need for object creation. Copies the function body directly to the call site. No Function object allocation. No extra class generated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

               //The inline keyword tells the compiler to copy the code of the HOF and the lambda directly into the call site, eliminating the need for object creation.
               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Inlining is a feature in Kotlin that optimizes the execution of higher-order functions by removing the runtime overhead of function calls. When a higher-order function is annotated with the inline keyword, the Kotlin compiler substitutes the function call with the actual code of the function at the call site. This eliminates the function call overhead and can lead to performance enhancements.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Best way: Higher order functions (lambdas) are stored as an object and saving objects requires memory. Imagine that you are preparing your lunch-bag for school and you have some salad, a sandwich an apple and three food containers.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "The higher-order functions or lambda expressions, all stored as an object so memory allocation, for both function objects and classes, and virtual calls might introduce runtime overhead. Sometimes we can eliminate the memory overhead by inlining the lambda expression.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "In order to reduce the memory overhead of such higher-order functions or lambda expressions, we can use the inline keyword which ultimately requests the compiler to not allocate memory and simply copy the inline code of that function at the calling place.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Pros: Boost performance by eleminating fun call and reducing overhead enhance the efficiency of higher-order functions by avoiding the creation of function objects for lambda expressions.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }

               Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                   SpanText(
                       text = "Cons: Inlining can lead to larger code size due to code duplication Inlining large functions can increase the compilation time.",
                       modifier = Modifier
                           .fontSize(FontSize.Medium)
                   )
               }
           }

            KotlinCode(
                code = """
                     inline fun calculateResult(a: Int, b: Int, operation: (Int, Int) -> Int): Int {
                         return operation(a, b)
                     }

                     fun main() {
                         val result = calculateResult(5, 3) { x, y -> x + y }
                         println(result) // Output: 8
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //if inline remove memory allocate otherwise inline function crate not memory allocate
                    fun main(){
                        message { print("This is message") }
                    }

                    inline fun message(a: () -> Unit){
                        a.invoke()
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                     //inline and non-inline both are use
                     fun main(){
                         end({println("End")}) {
                             print("xyz")
                         }
                     }

                     inline fun end(abc: () -> Unit, noinline  xyz: () -> Unit){
                         abc()
                         xyz()
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @InlineOnly
                    @SinceKotlin("1.3")
                    public inline fun <R, T> Result<T>.fold(
                        onSuccess: (value: T) -> R,
                        onFailure: (exception: Throwable) -> R
                    ): R {
                        contract {
                            callsInPlace(onSuccess, InvocationKind.AT_MOST_ONCE)
                            callsInPlace(onFailure, InvocationKind.AT_MOST_ONCE)
                        }
                        return when (val exception = exceptionOrNull()) {
                            null -> onSuccess(value as T)
                            else -> onFailure(exception)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "How does the inline function work in Kotlin?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An inline function is a function that is replaced at the call site with the body of the function. This can improve performance by avoiding the overhead of a function call. Inline functions are useful for functional programming constructs such as higher-order functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     inline fun <T> applyToList(list: List<T>, operation: (T) -> Unit) {
                         for (element in list) {
                             operation(element)
                         }
                     }
                     
                     //Here is an example of how this inline function can be used:
                     val myList = listOf(1, 2, 3, 4, 5)
                     applyToList(myList) { println(it) }
                """.trimIndent()
            )

            SpanText(
                text = "Why do we need inline functions?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A new stack frame is created.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Parameters are passed to the function.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "After the function executes, control returns to the caller.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, for higher-order functions, passing lambda expressions as parameters can lead to performance overhead, as these lambdas are internally compiled into objects. Inline functions eliminate this overhead by directly substituting the lambda into the code. Use the inline keyword when you pass functions as parameters to avoid unnecessary object creation and improve performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }



            Li(Modifier.padding(top = 1.em).toAttrs()) {
                SpanText(
                    text = "When should you consider using an inline function?",
                    modifier = Modifier.fontSize(FontSize.Large)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Performance Optimization: The function is small and called frequently.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Avoids Lambda Object Creation: When passing lambdas (anonymous functions) to higher-order functions, Kotlin creates an extra object. Inlining prevents this by inlining the lambda code, which can reduce memory allocations (Lambdas passed to inline functions are not allocated as objects, leading to less memory usage).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Need to use reified type parameters: reified modifier is also used in many libraries and util functions we define ourselves. The example below presents a common implementation of fromJsonOrNull that uses the Gson library. It also presents how the Koin library uses this kind of function to simplify both dependency injection and module declaration.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Access to Non-Local Returns: With inline functions, you can use a return statement inside a lambda that returns from the outer function, which is not possible with regular functions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Working with suspending functions in specific contexts.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Li(Modifier.padding(top = 1.em).toAttrs()) {
                SpanText(
                    text = "Avoid Inline Functions When",
                    modifier = Modifier.fontSize(FontSize.Large)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Function is Large: Inlining large functions can increase the size of the generated code (binary size).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You Don’t Need to Optimize: For functions that are not called often or don’t need optimization, the default function call is sufficient.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You’re Using Recursion: Inline functions cannot be recursive because inlining would lead to infinite expansion.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When Using Inline with Generic Types: In some cases, using generics with inline functions can lead to less optimal performance.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Li(Modifier.padding(top = 1.em).toAttrs()) {
                SpanText(
                    text = "What is a potential drawback of overusing inline functions?",
                    modifier = Modifier.fontSize(FontSize.Large)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Overusing inline functions can lead to increased bytecode size, especially if the inlined functions are large or called in many places. This can result in larger executable files and potentially slower loading times.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Noinline - (Opting Out Individual Lambdas)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Why? Sometimes you need to take the lambda parameter and pass it to another standard (non-inline) function, or save it to a variable for later use. Because an inline lambda is just copy-pasted code (not a real object), you cannot save it to a variable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Why? Sometimes you need to take the lambda parameter and pass it to another standard (non-inline) function, or save it to a variable for later use. Because an inline lambda is just copy-pasted code (not a real object), you cannot save it to a variable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        ❌ without noinline
                        //When a function is marked inline, Kotlin inlines all lambda parameters passed to it by default. But an inlined lambda does not exist as an object at runtime. If you need to store or pass a lambda as a real object reference, inlining breaks.
                        
                        inline fun process(
                            inlinedAction: () -> Unit,
                            storedAction: () -> Unit
                        ) {
                            inlinedAction()
                            val list = mutableListOf<() -> Unit>()
                            list.add(storedAction) // ❌ COMPILE ERROR: Illegal usage of inline-parameter
                            
                            Thread(storedAction).start() // ❌ COMPILE ERROR: Cannot pass inlined lambda as an object
                        }
                        
                        ✅ with noinline
                        //noinline tells the compiler: "Keep this function inlined overall, but leave this specific lambda parameter as a real Function object at runtime.
                        
                        inline fun process(
                            inlinedAction: () -> Unit,
                            noinline storedAction: () -> Unit // ✅ Kept as a real object
                        ) {
                            inlinedAction() // Pasted directly at call site
                            val list = mutableListOf<() -> Unit>()
                            list.add(storedAction) // ✅ WORKS: Stored in list
                            Thread(storedAction).start() // ✅ WORKS: Passed to Thread Runnable
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Fix: You mark that specific lambda parameter as noinline, telling the compiler: Go ahead and inline the rest of the function, but keep this specific lambda as a real object in memory because I need to store it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s like telling the compiler, “Inline this function, but not this part.”",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "noinline keyword is used to indicate that a lambda function passed as an argument to another function should not be inlined. This means that these parameter lambda functions behave as regular functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     fun main() {
                         message {
                             print("This is message a")
                             return@message
                         }
                     }

                     //You want to represent this A as a behave as regular function.
                     inline fun message(noinline a: () -> Unit){
                         a.invoke()
                     }
                """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "noinline lets you selectively prevent inlining for specific lambda parameters so they can exist as real function objects, while other lambdas in the same inline function are still inlined.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     inline fun execute(
                         task: () -> Unit,
                         noinline logger: () -> Unit
                     ) {
                         register(logger)   //❗needs a function object
                         task()
                     }
                """.trimIndent()
            )

            SpanText(
                text = "Crossinline - (Safe Inlining in Nested Contexts)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Danger: In an inline function, because the code is copy-pasted directly into the caller, if the developer writes a bare return inside the lambda, it doesn't just return from the lambda—it mathematically forces the entire outer calling function to return instantly (a Non-Local Return).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Fix: If your inline function takes the lambda and executes it inside a separate thread or an asynchronous callback, a Non-Local Return would crash the app logically. You mark the parameter as crossinline. This compiler enforcement explicitly forbids the developer from writing a bare return inside the lambda, guaranteeing the async flow cannot be hijacked.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Crossinline keyword is a helper to traverse scopes of two high-order functions (one is inlined and the other one is not). The crossinline modifier in Kotlin is used in the context of a higher-order function to indicate that the passed lambda expression cannot contain non-local returns.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Function should be inlined but non-local return is not allowed. We use it when this function is used in another scope where non-local return is not allowed; for instance, in another lambda that is not inlined.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When we add the crossinline, then it will not allow us the put the return inside that lambda.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Useful when you want to be extra careful and make sure that the code in the lambda doesn’t try to exit the whole function it’s in.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s a safety feature to prevent unexpected exits, especially when the lambda execution is happens in a different context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This ensures that the lambda expression is executed within the calling context and cannot abruptly terminate or return from the enclosing function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "crossinline allows a lambda to be inlined while prohibiting non-local returns, ensuring safe control flow when the lambda is executed in another context like a thread or callback.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    ❌ without crossinline
                    inline fun runInThread(action: () -> Unit) {
                        Thread {
                            action() // ❌ COMPILE ERROR: Can't inline 'action' inside a nested execution context
                        }.start()
                    }

                    fun main() {
                        runInThread {
                            return // DANGER: If allowed, this would try to non-local return from main() inside a background thread!
                        }
                    }
                    
                    ✅ with crossinline
                    inline fun runInThread(crossinline action: () -> Unit) {
                        Thread {
                            action() // ✅ WORKS: Safe to inline inside Runnable.run()
                        }.start()
                    }

                    fun main() {
                        runInThread {
                            // return // ❌ COMPILE ERROR: Forbidden by crossinline!
                            println("hello") // ✅ WORKS: Safe code runs normally
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "When to use Crossinline",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you want to ensure that a lambda passed to an inline function doesn’t accidentally break the control flow of the function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you are writing library functions that accept lambdas and you want to enforce specific behavior.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Normally, when we use a non-inline higher-order function inside of an inlined function we are not allowed to use lambda of inlined function in non-inline’s lambda scope and to maintain that purpose we can use crossinline keyword before our inlined function’s lambda.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    inline fun firstHigherOrderFunction(lambda1: (String) -> Unit) {
                        secondHigherOrderFunction {
                            lambda1("") // compile time error
                        }
                    }

                    fun secondHigherOrderFunction(lambda2: () -> Unit) {
                        lambda2()
                    }
                    
                    //crossline
                    inline fun firstHigherOrderFunction(crossline lambda1: (String) -> Unit) {
                        secondHigherOrderFunction {
                            lambda1("")
                        }
                    }

                    fun secondHigherOrderFunction(lambda2: () -> Unit) {
                        lambda2()
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "firstHigherOrderFunction is an inline function that takes a lambda lambda1 and passes it to secondHigherOrderFunction. secondHigherOrderFunction executes the lambda passed to it. The crossinline keyword ensures that lambda1 cannot cause a non-local return, maintaining the integrity of the control flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     inline fun higherOrderFunction(crossinline lambda: () -> Unit) {
                         val runnable = Runnable {
                             lambda()
                         }
                         runnable.run()
                     }

                     fun main() {
                         higherOrderFunction {
                             // Non-local return is not allowed here
                             return@higherOrderFunction
                         }
                     }
                """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the provided example, the higherOrderFunction is declared as inline and accepts a lambda parameter. This lambda is executed within a Runnable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By utilizing the crossinline modifier, the lambda expression is restricted from containing a non-local return. If a return statement is employed within the lambda, it must be labeled to specify the intended return target.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     fun main(){
                         message {
                             print("This is message a")
                             return@message
                         }
                     }

                     inline fun message(crossinline a: () -> Unit){
                         a.invoke()
                     }

                     //no invoke
                     fun main(){
                         teach {
                             print("This is message a")
                             return@teach
                         }
                     }

                     inline fun teach(crossinline abc: () -> Unit) {
                         abc()
                     }
                """.trimIndent()
            )

            //https://github.com/dalafiarisamuel/Unsplash-KMP
            KotlinCode(
                code = """
                     abstract class MviViewModel<Event, State>(defaultState: State) : ViewModel() {

                         protected val events = MutableSharedFlow<Event>()

                         var state by mutableStateOf(defaultState)
                             protected set

                         fun dispatch(event: Event) {
                             viewModelScope.launch { events.emit(event) }
                         }

                         protected inline fun <reified SpecificEvent : Event> on(
                             crossinline handle: suspend (event: SpecificEvent) -> Unit,
                         ) {
                             events
                                 .filterIsInstance<SpecificEvent>()
                                 .map { runCatching { handle(it) } }
                                 .map { it.exceptionOrNull() }
                                 .filterNotNull()
                                 .onEach(this::onError)
                                 .launchIn(viewModelScope)
                         }

                         protected open fun onError(throwable: Throwable) = Unit
                     }
                """.trimIndent()
            )

            SpanText(
                text = "Why do we need to use inline with reified in Android development?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "inline",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Copy-paste the body.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Enables non-local returns.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No lambda object",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No invoke() call",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No function call",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "noinline",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stored",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Returned",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Passed to another function",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Invoked later",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "crossinline",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The lambda executes inside another object.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The lambda runs in a coroutine.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The lambda runs in a coroutine.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The lambda executes asynchronously.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to prevent callers from relying on non-local returns.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Do We Need Inlining?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Every non-inlined lambda is instantiated as an object.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    ❌ without inline
                    // Normal higher-order function
                    fun doSomething(action: () -> Unit) {
                        action()
                    }

                    fun main() {
                        doSomething { println("hello") }
                    }
                    
                    
                    ✅ with inline
                    // Adding 'inline' tells the compiler to copy-paste the body
                    inline fun doSomething(action: () -> Unit) {
                        action()
                    }

                    fun main() {
                        doSomething { println("hello") }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Non-Local Returns",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    ❌ without inline
                    //Non-inlined lambdas run inside a separate Function object context. Because of this boundary, you cannot use return to exit the enclosing function.
                    
                    fun findFirst(list: List<Int>, predicate: (Int) -> Boolean): Int? {
                        for (item in list) {
                            if (predicate(item)) return item
                        }
                        return null
                    }
                    
                    fun main() {
                        val result = findFirst(listOf(1, 2, 3)) { item ->
                            if (item == 2) return item // ❌ COMPILE ERROR: 'return' is not allowed here
                            item > 1
                        }
                    }
                    
                    ✅ with inline
                    //Because an inline function's code is pasted directly into the calling location, a return statement acts as if it were written directly inside the caller. This is called a non-local return.
                    
                    inline fun findFirst(list: List<Int>, predicate: (Int) -> Boolean): Int? {
                        for (item in list) {
                            if (predicate(item)) return item
                        }
                        return null
                    }

                    fun main() {
                        val result = findFirst(listOf(1, 2, 3)) { item ->
                            if (item == 2) return item // ✅ WORKS: Exits main() directly!
                            item > 1
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "reified allows us to access the actual generic type at runtime, but it works only with inline because the compiler needs to replace T with the real type at the call site.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    𝘪𝘯𝘭𝘪𝘯𝘦 𝘧𝘶𝘯 <𝘳𝘦𝘪𝘧𝘪𝘦𝘥 𝘛> 𝘎𝘴𝘰𝘯.𝘧𝘳𝘰𝘮𝘑𝘴𝘰𝘯(𝘫𝘴𝘰𝘯: 𝘚𝘵𝘳𝘪𝘯𝘨): 𝘛 {
                      𝘳𝘦𝘵𝘶𝘳𝘯 𝘧𝘳𝘰𝘮𝘑𝘴𝘰𝘯(𝘫𝘴𝘰𝘯, 𝘛::𝘤𝘭𝘢𝘴𝘴. 𝘫𝘢𝘷𝘢)
                    }
                    """.trimIndent()
            )
        }
    }
}

//inline, noinline, crossinline — when and why? inline copies the function body + lambda to call sites (removes lambda object allocation, enables reified and non-local returns). nointine opts a specific lambda out (so it can be stored/passed around). crossinline forbids non-local returns in a lambda that will be called from another execution context.
//https://medium.com/@huseyinozkoc/kotlin-higher-order-functions-29e1107bfb00
//https://medium.com/dev-genius/kotlin-lambda-with-receiver-c8de99364201
//https://medium.com/@guruprasadhegde4/kotlin-inline-functions-2c0933fce585
//https://medium.com/@nematengg/beyond-inline-mastering-noinline-and-crossinline-in-kotlin-587a39641293
//https://medium.com/@hsinha610/kotlin-inlining-masterclass-inline-noinline-and-crossinline-demystified-7e6b7731d774